import { Upload, Sparkles, Send } from "lucide-react";
import { motion } from "framer-motion";
import { Section } from "@/components/layout/Section";
import { Container } from "@/components/layout/Container";
import { AnimatedSection } from "@/components/ui/animated-section";
import { staggerContainer, staggerItem } from "@/lib/animations";

const steps = [
  {
    icon: Upload,
    step: "1",
    title: "Upload Your Document",
    description: "Drag and drop your PDF, Word doc, or image. We support all common formats including scanned documents."
  },
  {
    icon: Sparkles,
    step: "2",
    title: "AI Extracts Questions",
    description: "Our AI analyzes your document, identifies questions, detects answer types, and creates a structured survey."
  },
  {
    icon: Send,
    step: "3",
    title: "Review & Distribute",
    description: "Fine-tune your survey, add logic if needed, then share via link, email, or embed on your website."
  },
];

export function HowItWorks() {
  return (
    <Section background="muted">
      <Container>
        <AnimatedSection className="text-center mb-12 md:mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground">
            How It <span className="text-gradient">Works</span>
          </h2>
          <p className="mt-4 text-lg text-muted-foreground max-w-2xl mx-auto">
            Three simple steps to transform any document into a digital survey
          </p>
        </AnimatedSection>
        
        {/* Responsive: stack on mobile, row on desktop */}
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
        >
          {steps.map((step, index) => (
            <motion.div 
              key={index} 
              className="relative text-center"
              variants={staggerItem}
            >
              {/* Connector line (hidden on mobile, shown between items on desktop) */}
              {index < steps.length - 1 && (
                <div className="hidden md:block absolute top-12 left-[60%] w-[80%] h-0.5 bg-border" />
              )}
              
              {/* Step circle with hover effect */}
              <motion.div 
                className="relative mx-auto h-24 w-24 rounded-full bg-primary/10 flex items-center justify-center mb-6 transition-colors hover:bg-primary/20"
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.2 }}
              >
                <step.icon className="h-10 w-10 text-primary" />
                <div className="absolute -top-2 -right-2 h-8 w-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold shadow-lg">
                  {step.step}
                </div>
              </motion.div>
              
              <h3 className="text-xl font-semibold text-foreground mb-2">
                {step.title}
              </h3>
              <p className="text-muted-foreground">
                {step.description}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </Container>
    </Section>
  );
}
